import type { Contact, ContactType, FlagKey } from '@doneverse/shared'
import { isStepRouteReachable } from '@/lib/onboardingLock'

/**
 * The founder product tour — ClickUp 86d3uknyj (Onboarding 07, step 4).
 *
 * Everything here is pure: the seven stops, whether each one's destination is
 * actually available to this founder, and which anchor a stop spotlights. The
 * component (`components/onboarding/ProductTour`) owns the DOM and the
 * completion call; this module owns the policy, so both can be tested without
 * the other.
 *
 * Two rules shape the whole thing:
 *
 *   1. **All seven stops, always.** Community, Doer University and the support
 *      room each sit behind their own rollout flag (all default OFF), and a
 *      step-4 founder often has no doer yet — so on a fresh account most of the
 *      tour is "not yours yet". Silently dropping those stops would hand
 *      different founders differently-sized tours and hide half the product
 *      from exactly the people who have not seen it. Every stop is presented;
 *      an unavailable one says where it lives instead of pointing at it.
 *   2. **Never spotlight something that is not there.** A stop resolves its
 *      anchor against the live DOM through an ordered chain, ending at anchors
 *      that always exist (the rail on desktop, the hamburger on mobile). If
 *      nothing in the chain is on screen the stop still renders — centred, with
 *      no cutout — rather than framing an empty rectangle.
 */

/**
 * The anchor ids. Stable names, never CSS-module class names: those are hashed
 * per build and free to change with any styling edit, so a tour keyed on them
 * breaks silently and invisibly.
 *
 * Each is claimed by exactly one component, listed beside it, which registers
 * its node through `TourAnchorContext`.
 */
export const TOUR_ANCHORS = {
  /** IconRailLayout — the rail itself. Always mounted; off-canvas on mobile. */
  rail: 'rail',
  /** PageHeader — the mobile hamburger. Only rendered below the lg breakpoint. */
  mobileNav: 'mobile-nav',
  /** IconRailLayout — rail icons. */
  railMessages: 'rail-messages',
  railRoadmap: 'rail-roadmap',
  railCoachingCommunity: 'rail-coaching-community',
  railTrainingTools: 'rail-training-tools',
  /** SectionSidebar, via founderNavConfig — the roadmap section's items. */
  sidebarBlueprint: 'sidebar-blueprint',
  sidebarPlaybooks: 'sidebar-playbooks',
  sidebarTasks: 'sidebar-tasks',
  sidebarFiles: 'sidebar-files',
  /**
   * ProductTour's own placeholder, pinned where the Coach Grace launcher lives.
   *
   * She is deliberately not mounted while onboarding is locked (see
   * CoachGraceWidget), and the tour runs entirely inside that lock — so the real
   * launcher never exists to point at. A tour-owned proxy in her spot shows the
   * founder where she will be without switching her on.
   */
  coachGraceProxy: 'coach-grace-proxy',
} as const

/**
 * Everything the tour is willing to point at. A stop can only name one of these,
 * so an id that no component claims is a type error rather than a stop that
 * silently falls back to the rail.
 */
export type TourAnchorId = (typeof TOUR_ANCHORS)[keyof typeof TOUR_ANCHORS]

/**
 * The anchors every stop falls back to when its own targets are off screen.
 *
 * Ordered for the responsive case: on desktop the rail is a sticky column and
 * wins; on mobile it is translated off-canvas (so it fails the visibility test)
 * and the hamburger that opens it wins instead.
 */
export const TOUR_FALLBACK_ANCHORS: readonly TourAnchorId[] = [TOUR_ANCHORS.rail, TOUR_ANCHORS.mobileNav]

export interface TourStop {
  id: string
  title: string
  /** What this part of the product does. True whether or not it is switched on. */
  body: string
  /**
   * Where to find it, in words. Shown when the destination is not available, so
   * the founder still learns the shape of the product from a stop that cannot
   * point at anything real.
   */
  location: string
  /**
   * What the founder gets once it IS available.
   *
   * Deliberately never phrased as "as soon as you finish onboarding": these
   * flags are an org-level rollout decision that the unlock does not change, and
   * a tour that promises a surface the founder will not get the moment they
   * finish is worse than one that stays vague (decision #11).
   */
  whenAvailable: string
  /** Ordered anchor candidates, most specific first. */
  anchors: readonly TourAnchorId[]
  /**
   * The surface this stop describes, when it is a route. Used only to decide
   * availability — the tour never links anywhere, because every one of these
   * routes is blocked by the onboarding lock the tour runs inside.
   */
  route?: string
  /** The stop needs someone to talk to — Messages is empty without a doer. */
  requiresDoer?: boolean
}

/**
 * The canonical seven stops, in brief order (ClickUp 86d3uknz4).
 *
 * The parent inventory once also listed the Masterclass Vault; the delivery
 * ticket's list is the one implemented, and the count is derived from this array
 * rather than written down anywhere, so adding or removing a stop is an edit
 * here and nothing else.
 */
export const TOUR_STOPS: readonly TourStop[] = [
  {
    id: 'doer-chat',
    title: 'Chat with your Doer',
    body: 'Messages is your direct line to the Doer working on your business — briefs, questions, feedback, all in one thread.',
    location: 'the Messages icon in the left rail',
    whenAvailable: 'Once your Doer is assigned, they appear here and you can say hello.',
    anchors: [TOUR_ANCHORS.railMessages],
    route: '/messages',
    requiresDoer: true,
  },
  {
    id: 'coach-grace',
    title: 'Meet Coach Grace AI',
    body: 'Coach Grace AI is your AI business coach. Ask her about strategy, brief a playbook by voice, or think out loud when you are stuck.',
    location: 'the bottom-right corner of every page',
    whenAvailable: 'She appears in this corner the moment your dashboard opens.',
    anchors: [TOUR_ANCHORS.coachGraceProxy],
  },
  {
    id: 'tasks-and-files',
    title: 'Tasks and files',
    body: 'Everything your Doer is working on, and every file the two of you share, live side by side in your Doing Roadmap section.',
    location: 'Tasks and Files in the left-hand menu',
    whenAvailable: 'They fill up as soon as your Doer starts work.',
    anchors: [TOUR_ANCHORS.sidebarTasks, TOUR_ANCHORS.sidebarFiles, TOUR_ANCHORS.railRoadmap],
    route: '/tasks',
  },
  {
    id: 'playbooks-blueprint',
    title: 'Playbooks and your Blueprint',
    body: 'Your Doing Blueprint is the seven-step roadmap for your business. Playbooks are the ready-made pieces of work you hand your Doer to move along it.',
    location: 'Doing Blueprint and Doing Playbooks in the left-hand menu',
    whenAvailable: 'Your recommended playbooks are waiting there.',
    anchors: [TOUR_ANCHORS.sidebarPlaybooks, TOUR_ANCHORS.sidebarBlueprint, TOUR_ANCHORS.railRoadmap],
    route: '/playbooks',
  },
  {
    id: 'community',
    title: 'The Founder community',
    body: 'Community is where Founders swap what is working, ask each other questions and keep each other honest.',
    location: 'the Coaching + Community section of the left rail',
    whenAvailable: 'When it is switched on for your account, you can join the groups that match your stage.',
    anchors: [TOUR_ANCHORS.railCoachingCommunity],
    route: '/groups',
  },
  {
    id: 'doers-university',
    title: 'Doer University',
    body: 'Doer University is the training library that teaches you how to brief, lead and get the most out of your Doer.',
    location: 'the Training + Tools section of the left rail',
    whenAvailable: 'When it is switched on for your account, the full course catalogue opens up.',
    anchors: [TOUR_ANCHORS.railTrainingTools],
    route: '/doers-university',
  },
  {
    id: 'cx-support',
    title: 'Support when you need it',
    body: "The Founder's Lounge is where you reach a real person — the live support room, the FAQ and everything else our client success team keeps there.",
    location: "the Training + Tools section of the left rail, under Founder's Lounge",
    whenAvailable: 'When it is switched on for your account, the live support room is one click away.',
    anchors: [TOUR_ANCHORS.railTrainingTools],
    route: '/founders-lounge',
  },
]

export const TOUR_STOP_COUNT = TOUR_STOPS.length

/** What the tour needs to know about this founder to judge availability. */
export interface TourContext {
  hasFlag: (flag: FlagKey) => boolean
  /**
   * The founder has someone in Messages who is actually their doer. Read from
   * the contacts the rail already fetches rather than inferred from the
   * workspace, because an empty Messages screen is exactly what this asks about.
   */
  hasDoerContact: boolean
}

/**
 * Founder contact types that mean "a doer to talk to".
 *
 * `workspace` is the founder↔doer thread and `user` is the per-doer DM; both are
 * built from workspace membership, so neither exists before a doer is assigned.
 * `group_chat` deliberately is NOT here: an independent group chat is built for
 * every user regardless of membership, so counting contacts wholesale would tell
 * a doerless founder they had someone to message. `project` never reaches a
 * founder at all (see `getContacts`).
 */
const DOER_CONTACT_TYPES: readonly ContactType[] = ['workspace', 'user']

export function hasDoerConversation(contacts: readonly Pick<Contact, 'type'>[]): boolean {
  return contacts.some((contact) => DOER_CONTACT_TYPES.includes(contact.type))
}

/**
 * Whether this stop's destination is really available to this founder.
 *
 * Route-backed stops defer to {@link isStepRouteReachable} — the same predicate
 * the tracker's CTA and the lock's redirect use — so the tour cannot decide a
 * surface is live that the rest of onboarding treats as switched off.
 *
 * A stop with no route (Coach Grace) is not gated by anything: she has no flag
 * and no eligibility rule, she simply is not mounted until the shell unlocks.
 */
export function isTourStopAvailable(stop: TourStop, context: TourContext): boolean {
  if (stop.requiresDoer && !context.hasDoerContact) return false
  if (!stop.route) return true
  return isStepRouteReachable({ route: stop.route }, context.hasFlag)
}

/**
 * The anchor chain a stop tries, in order: its own targets, then the shared
 * fallbacks. Fallbacks are appended rather than repeated on every stop so "what
 * always exists" is stated once.
 *
 * Which link in the chain wins is decided later, against the live registry and
 * the viewport — a stop's own anchor can be unmounted (its rail section is
 * flagged off) or laid out off screen (the mobile rail's off-canvas transform),
 * and both fall through to the next candidate.
 */
export function tourStopAnchors(stop: TourStop): TourAnchorId[] {
  return [...stop.anchors, ...TOUR_FALLBACK_ANCHORS.filter((a) => !stop.anchors.includes(a))]
}
